/**
 * ServerTableSelect - Table Selection Component for Server Terminal
 * Features:
 * - My Open Tables section (shows tables this server is handling)
 * - Take Away quick action
 * - Inside/Outside table grid selection
 * - Custom table number input
 * - Guest count selector
 * - Real-time table status (available, occupied, mine)
 */
import { useState, useEffect } from 'react';
import { 
  ShoppingBag, Plus, Minus, ChevronRight, Users,
  ArrowLeft, UtensilsCrossed, Table, Trash2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function ServerTableSelect({ onSelectTable, onBack, onOpenTable, staffId }) {
  const [tableNumber, setTableNumber] = useState('');
  const [guestCount, setGuestCount] = useState(2);
  const [tablesStatus, setTablesStatus] = useState([]);
  const [myOpenTables, setMyOpenTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [showMyTables, setShowMyTables] = useState(false);
  const [clearingTables, setClearingTables] = useState(false);

  // Force clear all stuck tables for this server
  const handleClearAllMyTables = async () => {
    if (!window.confirm('Are you sure you want to force-clear ALL your open tables? This will cancel any unpaid orders.')) {
      return;
    }
    
    setClearingTables(true);
    try {
      // Clear each table individually and collect results
      const clearResults = [];
      for (const table of myOpenTables) {
        try {
          const res = await api.delete(`/admin/clear-table/${encodeURIComponent(table.table_number)}`, {
            headers: { 'X-Staff-ID': staffId }
          });
          clearResults.push({ table: table.table_number, success: true, cleared: res.data.cleared });
        } catch (err) {
          clearResults.push({ table: table.table_number, success: false, error: err.message });
        }
      }
      
      const successCount = clearResults.filter(r => r.success).length;
      const totalCleared = clearResults.filter(r => r.success).reduce((sum, r) => sum + (r.cleared || 0), 0);
      
      if (successCount > 0) {
        toast.success(`Cleared ${totalCleared} orders from ${successCount} tables!`);
      }
      
      // Clear local state immediately for instant UI feedback
      setMyOpenTables([]);
      setShowMyTables(false);
      
      // Wait a moment for DB write to propagate, then refresh
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh tables status
      const [statusRes, myTablesRes] = await Promise.all([
        api.get('/server/tables-status', { headers: { 'X-Staff-ID': staffId } }),
        api.get('/server/my-tables', { headers: { 'X-Staff-ID': staffId } })
      ]);
      setTablesStatus(statusRes.data.tables || []);
      
      // Only restore myOpenTables if backend still returns some (shouldn't happen)
      const remainingTables = myTablesRes.data.open_tables || [];
      if (remainingTables.length > 0) {
        console.warn('Some tables still showing after clear:', remainingTables);
        toast.warning(`${remainingTables.length} table(s) still showing - may need manual refresh`);
        setMyOpenTables(remainingTables);
        setShowMyTables(true);
      }
    } catch (err) {
      console.error('Failed to clear tables:', err);
      toast.error('Failed to clear tables: ' + (err.response?.data?.detail || err.message));
    } finally {
      setClearingTables(false);
    }
  };

  // Fetch tables status on mount AND whenever this component mounts
  // (not just when staffId changes)
  useEffect(() => {
    const fetchTablesStatus = async () => {
      if (!staffId) return;
      try {
        setLoadingTables(true);
        const [statusRes, myTablesRes] = await Promise.all([
          api.get('/server/tables-status', { headers: { 'X-Staff-ID': staffId } }),
          api.get('/server/my-tables', { headers: { 'X-Staff-ID': staffId } })
        ]);
        
        setTablesStatus(statusRes.data.tables || []);
        setMyOpenTables(myTablesRes.data.open_tables || []);
        
        // Auto-expand My Open Tables if there are any
        if (myTablesRes.data.open_tables?.length > 0) {
          setShowMyTables(true);
        }
      } catch (err) {
        console.error('Failed to fetch tables status:', err);
        // Fallback - all tables available
        const insideTables = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(t => ({
          table_number: t, status: 'available', available: true
        }));
        const outsideTables = ['Outside 1', 'Outside 2', 'Outside 3', 'Outside 4', 'Outside 5'].map(t => ({
          table_number: t, status: 'available', available: true
        }));
        setTablesStatus([...insideTables, ...outsideTables]);
      } finally {
        setLoadingTables(false);
      }
    };
    
    fetchTablesStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run on EVERY mount, not just staffId change

  const handleDineInSubmit = () => {
    if (!tableNumber) {
      toast.error('Please select or enter a table number');
      return;
    }
    
    // Check if table is occupied by another server
    const tableStatus = tablesStatus.find(t => t.table_number === tableNumber);
    if (tableStatus && tableStatus.status === 'occupied' && !tableStatus.is_mine) {
      toast.error(`Table ${tableNumber} is being served by ${tableStatus.server_name}`);
      return;
    }
    
    onSelectTable({ 
      table: tableNumber, 
      guests: guestCount,
      orderType: 'dine-in'
    });
  };

  // TAKE AWAY - bypass table selection
  const handleTakeAway = () => {
    onSelectTable({ 
      table: null, 
      guests: 1,
      orderType: 'takeaway'
    });
  };

  // Handle clicking on an open table to add more items
  const handleOpenTableClick = (table) => {
    onOpenTable(table);
  };

  // Get table button style based on status
  const getTableStyle = (tableNum) => {
    const status = tablesStatus.find(t => t.table_number === tableNum);
    const isSelected = tableNumber === tableNum;
    
    if (isSelected) {
      return 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-400';
    }
    
    if (!status) {
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-100';
    }
    
    if (status.status === 'mine') {
      return 'bg-green-500 text-white hover:bg-green-600 ring-2 ring-green-300';
    }
    
    if (status.status === 'occupied') {
      return 'bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-300 cursor-not-allowed opacity-60';
    }
    
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30';
  };

  // Get table label
  const getTableLabel = (tableNum) => {
    const status = tablesStatus.find(t => t.table_number === tableNum);
    if (!status) return null;
    
    if (status.status === 'mine') {
      return <span className="text-xs block mt-0.5">${status.total_amount?.toFixed(0) || 0}</span>;
    }
    
    if (status.status === 'occupied') {
      return <span className="text-xs block mt-0.5">{status.server_name?.split(' ')[0] || 'Taken'}</span>;
    }
    
    return null;
  };

  // Inside tables 1-12
  const insideTables = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  
  // Outside tables
  const outsideTables = ['Outside 1', 'Outside 2', 'Outside 3', 'Outside 4', 'Outside 5'];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4" data-testid="server-table-select">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        {/* ========== MY OPEN TABLES - If server has active tables ========== */}
        {myOpenTables.length > 0 && (
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-4 mb-4 shadow-lg">
            <button
              onClick={() => setShowMyTables(!showMyTables)}
              className="w-full flex items-center justify-between text-white"
              data-testid="my-open-tables-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Table className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <span className="text-lg font-bold block">My Open Tables ({myOpenTables.length})</span>
                  <span className="text-green-100 text-sm">Tap to add more items</span>
                </div>
              </div>
              <ChevronRight className={`w-6 h-6 transition-transform ${showMyTables ? 'rotate-90' : ''}`} />
            </button>
            
            {showMyTables && (
              <div className="mt-4 space-y-2">
                {myOpenTables.map(table => (
                  <button
                    key={table.table_number}
                    onClick={() => handleOpenTableClick(table)}
                    className="w-full p-3 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-between transition-all"
                    data-testid={`open-table-${table.table_number}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/30 rounded-lg flex items-center justify-center font-bold text-lg">
                        {table.table_number}
                      </div>
                      <div className="text-left">
                        <span className="font-semibold block">{table.items_summary}</span>
                        <span className="text-green-100 text-sm">{table.time_display} • {table.order_count} order(s)</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold">${table.total_amount?.toFixed(2)}</span>
                      <Plus className="w-5 h-5 inline ml-2" />
                    </div>
                  </button>
                ))}
                
                {/* Force Clear Stuck Tables Button */}
                <button
                  onClick={handleClearAllMyTables}
                  disabled={clearingTables}
                  className="w-full mt-3 p-3 bg-red-500/80 hover:bg-red-600 text-white rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  data-testid="clear-stuck-tables-btn"
                >
                  {clearingTables ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                  <span className="font-semibold">Force Clear All Stuck Tables</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ========== TAKE AWAY BUTTON - PROMINENT ========== */}
        <button
          onClick={handleTakeAway}
          className="w-full mb-6 p-5 lg:p-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl shadow-lg flex items-center justify-center gap-4 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
          data-testid="server-takeaway-btn"
        >
          <ShoppingBag className="w-10 h-10 lg:w-12 lg:h-12" />
          <div className="text-left">
            <span className="text-2xl lg:text-3xl font-bold block">TAKE AWAY</span>
            <span className="text-blue-200 text-sm lg:text-base">Skip table selection</span>
          </div>
          <ChevronRight className="w-8 h-8 ml-auto" />
        </button>

        {/* ========== DINE-IN TABLE SELECTION ========== */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 mb-4">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Dine In - Select Table</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Choose a table for dine-in orders</p>
            </div>
          </div>

          {/* Inside Tables */}
          <div className="mb-3">
            <p className="text-xs lg:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              Inside Tables 
              {loadingTables && <span className="ml-2 text-xs">(loading...)</span>}
            </p>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2 lg:gap-3">
              {insideTables.map(t => {
                const status = tablesStatus.find(ts => ts.table_number === t);
                const isOccupiedByOther = status?.status === 'occupied' && !status?.is_mine;
                
                return (
                  <button
                    key={t}
                    onClick={() => !isOccupiedByOther && setTableNumber(t)}
                    disabled={isOccupiedByOther}
                    className={`p-3 lg:p-4 rounded-xl text-center font-bold transition-all min-h-[60px] lg:min-h-[72px] flex flex-col items-center justify-center ${getTableStyle(t)}`}
                    data-testid={`server-table-${t}`}
                    title={status?.status === 'occupied' ? `Served by ${status?.server_name}` : ''}
                  >
                    <span className="text-lg lg:text-xl">{t}</span>
                    {getTableLabel(t)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Outside Tables */}
          <div className="mb-4">
            <p className="text-xs lg:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Outside Tables</p>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 lg:gap-3">
              {outsideTables.map(t => {
                const status = tablesStatus.find(ts => ts.table_number === t);
                const isOccupiedByOther = status?.status === 'occupied' && !status?.is_mine;
                
                return (
                  <button
                    key={t}
                    onClick={() => !isOccupiedByOther && setTableNumber(t)}
                    disabled={isOccupiedByOther}
                    className={`p-3 lg:p-4 rounded-xl text-center font-medium transition-all min-h-[60px] lg:min-h-[72px] flex flex-col items-center justify-center ${getTableStyle(t)}`}
                    data-testid={`server-table-${t}`}
                    title={status?.status === 'occupied' ? `Served by ${status?.server_name}` : ''}
                  >
                    <span className="text-sm lg:text-base">{t.replace('Outside ', 'Out ')}</span>
                    {getTableLabel(t)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Table Number */}
          <div className="mb-4">
            <label className="block text-xs lg:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Or enter custom table
            </label>
            <Input
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="e.g., VIP1, Private"
              className="h-11 lg:h-12"
              data-testid="server-table-input"
            />
          </div>

          {/* Guest Count */}
          <div className="flex items-center justify-between mb-4 p-3 lg:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <span className="text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Users className="w-4 h-4 lg:w-5 lg:h-5" />
              Guests
            </span>
            <div className="flex items-center gap-3 lg:gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                className="h-10 w-10 lg:h-12 lg:w-12"
              >
                <Minus className="w-4 h-4 lg:w-5 lg:h-5" />
              </Button>
              <span className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white w-8 text-center">
                {guestCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setGuestCount(Math.min(20, guestCount + 1))}
                className="h-10 w-10 lg:h-12 lg:w-12"
              >
                <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
              </Button>
            </div>
          </div>

          <Button
            onClick={handleDineInSubmit}
            disabled={!tableNumber}
            className="w-full h-14 lg:h-16 text-lg lg:text-xl bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="server-continue-btn"
          >
            <UtensilsCrossed className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
            Dine In - Table {tableNumber || '?'}
            <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
